import { Button, IconHelp } from "@studyplus/boron-ui";
import * as React from "react";
import { PageHeader } from "../../components/atoms/MainSection";
import { ExaminationResultForm } from "../../components/features/ExaminationResultForm/ExaminationResultForm";
import { useExaminationResultForm } from "../../components/features/ExaminationResultForm/useExaminationResultForm";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbItemLink,
} from "../../components/general/Breadcrumb";
import { matchHttpError } from "../../errors";
import { OutletContextProps } from "../../helpers/RouterHelper";
import enhanceStudentsPage, {
  StudentsPageProps,
} from "../../hocs/enhanceStudentsPage";
import StudentInterface from "../../interfaces/StudentInterface";
import { useFetchStudentExaminationResult } from "../StudentsAnalyticsExaminationResultPage/useFetchStudentExaminationResult";
import { useNewExaminationResultFormEffect } from "./useNewExaminationResultFormEffect";

type Props = StudentsPageProps &
  OutletContextProps & {
    student: StudentInterface;
  };

const StudentsExaminationResultsNewPage_ = ({
  student,
  setActiveMenu,
}: Props) => {
  React.useEffect(() => {
    setActiveMenu("analytics");
  }, []);

  return (
    <>
      {/* パンくずの上下マージンは「戻る」リンク（BackLink）と揃えて 2rem に統一する */}
      <div className="my-8">
        <Breadcrumb>
          <BreadcrumbItem>
            <BreadcrumbItemLink
              href={`/students/${student.id}/analytics/examination`}
            >
              <div className="mr-4 text-lg">成績記録一覧</div>
            </BreadcrumbItemLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrent>
            <span className="ml-4">成績記録の登録</span>
          </BreadcrumbItem>
        </Breadcrumb>
      </div>

      <div className="relative rounded border border-solid border-gray-400 bg-white px-7 pb-7 pt-8">
        <PageHeader>成績記録の登録</PageHeader>
        <div className="mt-7">
          <Description />
        </div>
        <div className="mt-9">
          <NewExaminationResultForm student={student} />
        </div>
      </div>
    </>
  );
};

const Description = () => {
  return (
    <div className="flex items-center gap-4">
      <p>成績情報の入力は登録方法を確認しながら進めてください。</p>
      <a
        href="https://fs-help.studyplus.co.jp/ja/articles/7963949"
        className="text-blue-400"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="inline-flex items-center gap-1">
          <IconHelp />
          登録方法を見る
        </span>
      </a>
    </div>
  );
};

const NewExaminationResultForm = ({
  student,
}: {
  student: Props["student"];
}) => {
  const form = useExaminationResultForm({
    studentId: student.id,
    submitType: "create",
  });

  useNewExaminationResultFormEffect(form);
  const { registrationStatus, isLoading } = useExaminationResult({
    studentId: student.id,
    examinationId: form.values.examination?.id,
  });

  const resultDisabled =
    form.values.examination === null ||
    (!isLoading && registrationStatus === "registered");

  return (
    <>
      <ExaminationResultForm
        studentId={student.id}
        form={form}
        resultDisabled={resultDisabled}
        isExaminationSelectable={true}
      />
      <div className="sticky bottom-0 -mx-7 -mb-7 flex justify-end bg-white px-7 py-4">
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            form.handleSubmit(e as any);
          }}
          isLoading={form.isSubmitting}
          disabled={
            form.isSubmitting ||
            // 選択した試験に既存成績がある場合はdisabledにしているが、
            // 試験選択前は、とりあえず押してエラーに気づけるようにボタンを活性化している
            (resultDisabled && form.values.examination !== null) ||
            !form.isValid
          }
        >
          登録
        </Button>
      </div>
    </>
  );
};

// 選択中の試験の成績登録状況
type RegistrationStatus = "registered" | "unregistered";
const useExaminationResult = ({
  studentId,
  examinationId,
}: {
  studentId: string;
  examinationId?: string;
}) => {
  const [registrationStatus, setRegistrationStatus] =
    React.useState<RegistrationStatus | null>(null);
  const { isSuccess, isError, error, data, isLoading } =
    useFetchStudentExaminationResult({
      studentId,
      examinationId: examinationId ?? null,
      retry: false,
    });

  // APIからのレスポンスが404 NotFundのとき
  if (isError && error && registrationStatus !== "unregistered") {
    matchHttpError(error, {
      caseNotFound: () => {
        setRegistrationStatus("unregistered");
      },
    });
  }

  // データが取得できたとき
  if (isSuccess && data && registrationStatus !== "registered") {
    setRegistrationStatus("registered");
  }

  return {
    registrationStatus,
    examinationResult: data,
    isLoading,
  };
};

export const StudentsExaminationResultsNewPage = enhanceStudentsPage(
  StudentsExaminationResultsNewPage_,
  {
    title: "成績記録の登録",
  },
);
